import { logError } from '@zg-rentals/log-error';
import { RentalManagerUserPropertyDetailsWebApiHandler_details } from '@zg-rentals/ts-rental-manager-api-client';
import { ListingType, PropertyType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyCreateWebApiHandler_create } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PropertyConstants from '../../constants/PropertyConstants';

const {
  LISTING_TYPES: { RENTAL, ROOM, SALE },
  PROPERTY_TYPES: { COMMUNITY, CONDO, HOUSE, MULTIPLEX, TOWNHOUSE },
} = PropertyConstants;

const LISTING_TYPE_TO_ENUM: Record<string, ListingType> = {
  [RENTAL]: ListingType.RENTAL,
  [ROOM]: ListingType.ROOM,
  [SALE]: ListingType.SALE,
};

// Community maps to LARGE as the general-purpose community PropertyType
const PROPERTY_TYPE_TO_ENUM: Record<string, PropertyType> = {
  [COMMUNITY]: PropertyType.LARGE,
  [CONDO]: PropertyType.CONDO,
  [MULTIPLEX]: PropertyType.CONDO,
  [HOUSE]: PropertyType.HOUSE,
  [TOWNHOUSE]: PropertyType.TOWNHOUSE,
};

export type CreatePropertyFormData = {
  listingType: string;
  propertyType: string;
  fullAddress: string;
  unitNumber?: string;
  numUnits?: string;
  customerId?: string | null;
};

const useCreateProperty = () => {
  const { mutateAsync } = useRentalManagerUserPropertyCreateWebApiHandler_create();

  const createProperty = async (formData: CreatePropertyFormData) => {
    try {
      const result = await mutateAsync({
        getParams: {
          listingType: LISTING_TYPE_TO_ENUM[formData.listingType],
          propertyType: PROPERTY_TYPE_TO_ENUM[formData.propertyType],
          fullAddress: formData.fullAddress,
          ...(formData.unitNumber != null && { unitNumber: formData.unitNumber }),
          ...(formData.numUnits != null && { numUnits: Number(formData.numUnits) }),
          ...(formData.customerId != null && { customerId: formData.customerId }),
          includeWebAnalytics: true,
        },
      });

      // Fetch full property details to maintain parity with the old PostingActions.property.create
      const propertyDetails = await RentalManagerUserPropertyDetailsWebApiHandler_details({
        listingAlias: result.listingAlias,
      });

      return propertyDetails;
    } catch (error) {
      logError({
        error,
        context: 'useCreateProperty',
      });

      throw error;
    }
  };

  return { createProperty };
};

export { useCreateProperty };
